<template>
  <!--
    #offers sentinel: the Offers section is gone, but the id must stay in the DOM
    for the header anchors, App.vue hash navigation and the legacy BottomNav
    scroll-spy. Zero-height block so its bounding rect is still meaningful.
  -->
  <div id="offers" class="anchor-sentinel" aria-hidden="true"></div>

  <section id="services" class="services-section">
    <h2 class="section-title">{{ $t('offers.title') }}</h2>

    <!-- Skeleton while types / services load -->
    <div v-if="showSkeleton" class="services-grid" aria-hidden="true">
      <div v-for="n in 2" :key="n" class="service-card is-skeleton">
        <div class="service-media skeleton-block"></div>
        <div class="service-body">
          <div class="skeleton-line skeleton-line-title"></div>
          <div class="skeleton-line"></div>
          <div class="skeleton-line skeleton-line-short"></div>
        </div>
      </div>
    </div>

    <!-- Inline load error with an explicit retry -->
    <div v-else-if="hasLoadError" class="services-error" role="alert">
      <p class="services-error-text">{{ $t('booking.loadError') }}</p>
      <button type="button" class="services-retry" @click="retry">
        {{ $t('booking.retry') }}
      </button>
    </div>

    <div v-else class="services-grid">
      <article
        v-for="type in massageTypes"
        :key="type.id"
        class="service-card"
        :class="{ 'is-popular': type.popular }"
      >
        <div v-if="type.imageUrl" class="service-media">
          <img
            class="service-img"
            :src="type.imageUrl"
            :alt="type.name"
            loading="lazy"
            decoding="async"
          />
        </div>

        <div class="service-body">
          <h3 class="service-name">
            {{ type.name }}
            <span v-if="type.popular" class="popular-badge">{{ $t('services.popular') }}</span>
            <span v-if="type.pairMassage" class="pair-badge">{{ $t('services.pairBadge') }}</span>
          </h3>

          <template v-if="type.description">
            <p
              :id="`service-desc-${type.id}`"
              class="service-desc"
              :class="{ 'is-clamped': !isExpanded(type.id) }"
            >
              {{ type.description }}
            </p>
            <button
              type="button"
              class="service-more"
              :aria-expanded="isExpanded(type.id) ? 'true' : 'false'"
              :aria-controls="`service-desc-${type.id}`"
              @click="toggleDescription(type.id)"
            >
              {{ isExpanded(type.id) ? $t('services.less') : $t('services.more') }}
            </button>
          </template>

          <div
            v-if="durationsForType(type).length > 0"
            class="service-chips"
            role="group"
            :aria-label="$t('offers.selectDuration')"
          >
            <button
              v-for="service in durationsForType(type)"
              :key="service.id"
              type="button"
              class="service-chip"
              :aria-label="`${service.duration} ${$t('services.minutes')} · ${formatCurrencyWithSymbol(chipPrice(service, type))}`"
              @pointerdown="prefetchBookingSheet"
              @click="book(service, type)"
            >
              <span class="chip-duration">{{ service.duration }} {{ $t('common.min') }}</span>
              <span class="chip-sep" aria-hidden="true">·</span>
              <span class="chip-price">{{ formatCurrencyWithSymbol(chipPrice(service, type)) }}</span>
            </button>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useCurrency } from '@/composables/useCurrency'
import { useServices } from '@/composables/useServices'
import { useMassageTypes } from '@/composables/useMassageTypes'
import { useBookingSheet } from '@/composables/useBookingSheet'
import { priceFor, durationsFor } from '@/utils/pricing'

export default {
  name: 'ServicesSection',
  setup() {
    const { formatCurrencyWithSymbol, fetchSystemPreferences } = useCurrency()
    const {
      massageTypes,
      isLoading: typesLoading,
      error: typesError,
      load: loadMassageTypes
    } = useMassageTypes()
    const {
      services,
      isLoading: servicesLoading,
      error: servicesError,
      load: loadServices,
      reload: reloadServices
    } = useServices()
    const { open: openBookingSheet, prefetch: prefetchBookingSheet } = useBookingSheet()

    const expanded = ref([])

    const showSkeleton = computed(
      () => (typesLoading.value || servicesLoading.value) && massageTypes.value.length === 0
    )

    const hasLoadError = computed(
      () => massageTypes.value.length === 0 && Boolean(typesError.value || servicesError.value)
    )

    const isExpanded = (id) => expanded.value.includes(id)

    const toggleDescription = (id) => {
      expanded.value = isExpanded(id)
        ? expanded.value.filter((x) => x !== id)
        : [...expanded.value, id]
    }

    const durationsForType = (massageType) => durationsFor(massageType, services.value)

    const chipPrice = (service, massageType) => priceFor(service, massageType)

    const book = (service, massageType) => {
      openBookingSheet({
        serviceId: service.id,
        massageTypeId: massageType.id,
        source: 'chip'
      })
    }

    const retry = () => {
      reloadServices()
      loadMassageTypes()
    }

    onMounted(() => {
      fetchSystemPreferences()
      loadMassageTypes()
      loadServices()
      // Warm the booking-sheet chunk so the first chip tap opens instantly.
      prefetchBookingSheet()
    })

    return {
      massageTypes,
      services,
      showSkeleton,
      hasLoadError,
      isExpanded,
      toggleDescription,
      durationsForType,
      chipPrice,
      book,
      retry,
      formatCurrencyWithSymbol,
      prefetchBookingSheet
    }
  }
}
</script>

<style scoped>
/* Zero-height anchor target — must stay in flow so scroll-spy sees a real top */
.anchor-sentinel {
  height: 0;
  scroll-margin-top: 80px;
}

.services-section {
  padding: clamp(48px, 8vw, 64px) 0;
}

.section-title {
  width: min(100% - 2 * var(--gutter), 1200px);
  margin: 0 auto var(--s-6);
  text-align: center;
  font-size: var(--fs-h2);
  color: var(--color-heading);
  text-shadow: 1px 1px 3px var(--text-shadow-color);
}

/* Mobile: horizontal snap row. Desktop: 2 / 3 column grid (see media queries). */
.services-grid {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 85%;
  gap: var(--s-4);
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  scroll-padding-inline: var(--gutter);
  padding: 0 var(--gutter) var(--s-2);
  -webkit-overflow-scrolling: touch;
}

.service-card {
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  background: var(--surface-card);
  border: 1px solid var(--border-light);
  border-radius: var(--r-3);
  overflow: hidden;
  box-shadow: var(--shadow-secondary);
}

.service-card.is-popular {
  border: 2px solid var(--border-featured);
}

.service-media {
  aspect-ratio: 16 / 10;
  width: 100%;
  overflow: hidden;
  background: var(--surface-muted);
}

.service-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.service-body {
  display: flex;
  flex-direction: column;
  gap: var(--s-2);
  padding: var(--s-4);
}

.service-name {
  margin: 0;
  font-size: var(--fs-h3);
  font-weight: 600;
  color: var(--text-strong);
  line-height: 1.25;
}

.popular-badge {
  display: inline-block;
  background: var(--badge-bg);
  color: var(--badge-text);
  font-family: 'Nunito', sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  margin-left: 8px;
  vertical-align: middle;
  letter-spacing: 0.03em;
  white-space: nowrap;
}

/* Pair-massage badge: same shape as Popular but outline-only, so it stays
   visually subordinate. Painted by the card's existing border/text tokens. */
.pair-badge {
  display: inline-block;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--border-main);
  font-family: 'Nunito', sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 20px;
  margin-left: 8px;
  vertical-align: middle;
  letter-spacing: 0.03em;
  white-space: nowrap;
}

.service-desc {
  margin: 0;
  color: var(--text-secondary);
  font-size: var(--fs-body);
  line-height: 1.55;
}

.service-desc.is-clamped {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.service-more {
  align-self: flex-start;
  min-height: 44px;
  padding: 0 var(--s-2);
  margin-left: calc(var(--s-2) * -1);
  background: none;
  border: none;
  color: var(--color-primary);
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.service-chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--s-2);
  margin-top: var(--s-1);
}

.service-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 0 var(--s-3);
  border-radius: 50px;
  border: 2px solid var(--border-main);
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.95rem;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.chip-duration {
  font-weight: 600;
}

.chip-price {
  font-weight: 700;
}

.chip-sep {
  opacity: 0.6;
}

.service-chip:hover,
.service-chip:active {
  background: var(--gradient-brand);
  color: var(--text-on-gradient);
  border-color: transparent;
}

.service-chip:focus-visible,
.service-more:focus-visible,
.services-retry:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

/* Inline load error */
.services-error {
  width: min(100% - 2 * var(--gutter), 560px);
  margin: 0 auto;
  padding: var(--s-5) var(--gutter);
  text-align: center;
  background: var(--color-danger-soft);
  border: 1px solid var(--color-danger);
  border-radius: var(--r-2);
}

.services-error-text {
  margin: 0 0 var(--s-4) 0;
  color: var(--text-primary);
}

.services-retry {
  min-height: 44px;
  padding: 0 var(--s-5);
  border: none;
  border-radius: 50px;
  background: var(--gradient-brand);
  color: var(--text-on-gradient);
  font-family: inherit;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
}

/* Skeleton */
.service-card.is-skeleton {
  min-height: 260px;
}

.skeleton-block,
.skeleton-line {
  background: linear-gradient(
    90deg,
    var(--surface-muted) 25%,
    var(--surface-hover) 50%,
    var(--surface-muted) 75%
  );
  background-size: 200% 100%;
  animation: servicesShimmer 1.5s infinite;
}

.skeleton-line {
  height: 14px;
  border-radius: 7px;
  margin-bottom: var(--s-2);
}

.skeleton-line-title {
  height: 22px;
  width: 70%;
}

.skeleton-line-short {
  width: 50%;
}

@keyframes servicesShimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .skeleton-block,
  .skeleton-line {
    animation: none;
  }
}

/* Desktop: a centred track grid. auto-fit collapses the empty tracks, so a
   tenant with two massage types gets two centred cards instead of a hole where
   the third column would be. Track counting uses the 280px minimum, and the
   1140px cap keeps that at 2 columns from 768 and 3 columns from 1024 up. */
@media (min-width: 768px) {
  .services-grid {
    width: min(100% - 2 * var(--gutter), 1140px);
    margin: 0 auto;
    grid-auto-flow: row;
    grid-auto-columns: auto;
    grid-template-columns: repeat(auto-fit, minmax(280px, 360px));
    justify-content: center;
    gap: var(--s-5);
    overflow: visible;
    padding: 0;
    align-items: start;
  }
}
</style>
